/* global axios */
import ApiClient from './ApiClient';

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  email: 'e-mail',
  sms: 'SMS',
};

const normalizeValue = value =>
  String(value || '')
    .trim()
    .toLowerCase();

export const normalizeNotificationChannel = channel => {
  const normalized = normalizeValue(channel);
  return normalized === 'push' ? 'app' : normalized;
};

export const consentChannelFor = channel => {
  const normalized = normalizeNotificationChannel(channel);
  return normalized === 'app' ? 'PUSH' : normalized.toUpperCase();
};

const normalizePurpose = purpose =>
  String(purpose || '')
    .trim()
    .toUpperCase();

const normalizeProfile = profile =>
  String(profile || '')
    .trim()
    .toUpperCase();

const contactPhone = contact =>
  contact?.contactability?.whatsapp_phone_number ||
  contact?.contactability?.submitted_phone_number ||
  contact?.phone_number ||
  contact?.phoneNumber ||
  '';

const hasContactChannel = (contact, channel) => {
  const normalizedChannel = normalizeNotificationChannel(channel);

  if (normalizedChannel === 'email') return !!contact?.email;
  if (['whatsapp', 'sms'].includes(normalizedChannel))
    return !!contactPhone(contact);
  return true;
};

const findConsent = ({ consents = [], purpose, channel, profile }) => {
  const normalizedPurpose = normalizePurpose(purpose);
  const normalizedChannel = consentChannelFor(channel);
  const normalizedProfile = normalizeProfile(profile);

  return consents.find(consent => {
    const profileMatches =
      !normalizedProfile ||
      normalizeProfile(consent.profile) === normalizedProfile;

    return (
      normalizePurpose(consent.purpose) === normalizedPurpose &&
      consentChannelFor(consent.channel) === normalizedChannel &&
      profileMatches
    );
  });
};

export const evaluateContactNotificationPreference = (
  snapshot = {},
  { channel, purpose, profile = 'TENANT', contact = {}, isBlocked = false } = {}
) => {
  const normalizedChannel = normalizeNotificationChannel(channel);
  const label = CHANNEL_LABELS[normalizedChannel] || channel || 'canal';

  if (!normalizedChannel || !purpose) {
    return {
      allowed: false,
      reason: 'invalid_request',
      severity: 'error',
      message: 'Informe canal e finalidade antes de disparar.',
    };
  }

  if (!hasContactChannel(contact, normalizedChannel)) {
    return {
      allowed: false,
      reason: 'channel_missing',
      severity: 'warning',
      message: `Canal ausente: cadastre ${label} no perfil antes do disparo.`,
    };
  }

  if (normalizedChannel === 'whatsapp' && isBlocked) {
    return {
      allowed: false,
      reason: 'blocked_contact',
      severity: 'error',
      message:
        'Contato bloqueado: desbloqueie e registre o motivo antes de enviar WhatsApp.',
    };
  }

  const consent = findConsent({
    consents: snapshot.consents || [],
    purpose,
    channel: normalizedChannel,
    profile,
  });

  if (!consent) {
    return {
      allowed: false,
      reason: 'missing_consent',
      severity: 'warning',
      message: `Sem opt-in registrado para ${label} nesta finalidade.`,
    };
  }

  const status = normalizePurpose(consent.effective_status || consent.status);
  if (status === 'ACTIVE') {
    return {
      allowed: true,
      reason: 'opt_in',
      severity: 'success',
      message: `${label} liberado para esta finalidade.`,
      consent,
    };
  }

  return {
    allowed: false,
    reason: status === 'WITHDRAWN' ? 'opt_out' : 'pending_consent',
    severity: status === 'WITHDRAWN' ? 'error' : 'warning',
    message:
      status === 'WITHDRAWN'
        ? `Opt-out registrado para ${label} nesta finalidade.`
        : `Consentimento pendente para ${label} nesta finalidade.`,
    consent,
  };
};

export const notificationPreferenceErrorState = error => {
  const status = Number(error?.response?.status || error?.status) || null;

  if (status === 403) {
    return {
      kind: 'forbidden',
      canEdit: false,
      message:
        'Sem permissão para editar preferências de notificação deste contato.',
    };
  }

  if (status === 404) {
    return {
      kind: 'not_found',
      canEdit: false,
      message: 'Contato não encontrado para preferências de notificação.',
    };
  }

  return {
    kind: 'error',
    canEdit: true,
    message: 'Não foi possível carregar as preferências de notificação.',
  };
};

class NotificationsAPI extends ApiClient {
  constructor() {
    super('notifications', { accountScoped: true });
  }

  contactNotificationPreferencesPath(contactId) {
    return `${this.baseUrl()}/contacts/${contactId}/notification_preferences`;
  }

  get({ page, status, type, sortOrder }) {
    const includesFilter = [status, type].filter(value => !!value);

    return axios.get(this.url, {
      params: {
        page,
        sort_order: sortOrder,
        includes: includesFilter,
      },
    });
  }

  getNotifications(contactId) {
    return axios.get(`${this.url}/${contactId}/notifications`);
  }

  getContactNotificationPreferences(contactId) {
    return axios.get(this.contactNotificationPreferencesPath(contactId));
  }

  updateContactNotificationPreferences(
    contactId,
    preferences,
    { idempotencyKey } = {}
  ) {
    const url = this.contactNotificationPreferencesPath(contactId);
    const payload = { preferences };
    if (!idempotencyKey) return axios.patch(url, payload);

    return axios.patch(url, payload, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  getUnreadCount() {
    return axios.get(`${this.url}/unread_count`);
  }

  read(primaryActorType, primaryActorId) {
    return axios.post(`${this.url}/read_all`, {
      primary_actor_type: primaryActorType,
      primary_actor_id: primaryActorId,
    });
  }

  unRead(id) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  readAll() {
    return axios.post(`${this.url}/read_all`);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  deleteAll({ type = 'all' }) {
    return axios.post(`${this.url}/destroy_all`, {
      type,
    });
  }

  snooze({ id, snoozedUntil = null }) {
    return axios.post(`${this.url}/${id}/snooze`, {
      snoozed_until: snoozedUntil,
    });
  }
}

export default new NotificationsAPI();
